import React, { useCallback } from 'react';
import { Button } from 'src/@vymo/ui/atoms';
import { List, Popup } from 'src/@vymo/ui/blocks';
import { Keys, locale } from 'src/i18n';
import { HeaderButton, RichTextHeaderProps } from './types';
import styles from './index.module.scss';

function RichTextHeader({
  headerText,
  headerButtons,
  getEditor,
  onHeaderButtonClick,
  onClickOption,
  dataTestId,
  menuLabel,
  menuOptions = [],
}: RichTextHeaderProps) {
  const onButtonClick = useCallback(
    (event: React.MouseEvent) => {
      const type = event.currentTarget.getAttribute('data-type');
      const selectedButtonOption = headerButtons?.find(
        (option) => option.type === type,
      );
      onHeaderButtonClick(selectedButtonOption as HeaderButton);
    },
    [headerButtons, onHeaderButtonClick],
  );

  const handleDropdownItemClick = useCallback(
    (idx: number) => {
      const editor = getEditor();
      if (!editor) return;
      const index = Math.max(0, editor.getLength() - 1);
      const option = menuOptions[idx];
      onClickOption(editor, index, option, true);
    },
    [menuOptions, onClickOption, getEditor],
  );

  const getDropdownElement = useCallback(
    () => (
      <Popup
        closeTrigger="click"
        content={
          <List onItemClick={(idx) => handleDropdownItemClick(idx)}>
            {menuOptions.map((input) => (
              <List.Item>{input.name}</List.Item>
            ))}
          </List>
        }
      >
        <Button>{menuLabel || locale(Keys.SELECT_VALUE)}</Button>
      </Popup>
    ),
    [menuOptions, handleDropdownItemClick, menuLabel],
  );

  const getButtonElement = useCallback(
    (buttonOption: HeaderButton) => {
      const {
        label,
        type,
        buttonType = 'text',
        buttonVariant = 'filled',
      } = buttonOption;
      return (
        <Button
          type={buttonType}
          variant={buttonVariant}
          className={styles.richTextEditor__header__button}
          data-type={type}
          onClick={onButtonClick}
        >
          {label}
        </Button>
      );
    },
    [onButtonClick],
  );

  return (
    <div
      className={styles.richTextEditor__header}
      data-test-id={`${dataTestId}-header`}
    >
      {headerText && <div>{headerText}</div>}
      {headerButtons?.map((buttonConfig) => getButtonElement(buttonConfig))}
      {menuOptions && getDropdownElement()}
    </div>
  );
}

export default RichTextHeader;
